import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { docInScope, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';
import StatCards from './StatCards';
import './AdminRoomsView.css';

function AdminRoomsView({ adminData }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', building: '', capacity: '' });
  const [error, setError] = useState('');

  const schoolIds = useMemo(() => adminSchoolIds(adminData), [adminData]);

  const loadRooms = useCallback(async () => {
    if (!adminData) return;
    setLoading(true);
    try {
      const ck = (p) => `${schoolIds.join('|') || 'all'}:${p}`;
      const roomDocs = await dataCacheService.getDocs(ck('rooms'), collection(db, 'rooms'));
      setRooms(roomDocs
        .filter(r => docInScope(r.schoolId, schoolIds))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (e) {
      console.error('🔥 AdminRooms: load error', e);
    } finally {
      setLoading(false);
    }
  }, [adminData, schoolIds]);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  const resetForm = () => {
    setForm({ name: '', building: '', capacity: '' });
    setEditing(null);
    setShowForm(false);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Room name is required'); return; }
    try {
      const payload = {
        name: form.name.trim(),
        building: form.building.trim() || null,
        capacity: form.capacity !== '' ? parseInt(form.capacity, 10) : null,
        schoolId: adminData?.schoolId || null,
        isActive: true,
      };
      if (editing) {
        await updateDoc(doc(db, 'rooms', editing.id), payload);
      } else {
        const id = crypto.randomUUID();
        await setDoc(doc(db, 'rooms', id), {
          ...payload, id, createdAt: new Date().toISOString(),
        });
      }
      dataCacheService.invalidatePath('rooms');
      resetForm();
      loadRooms();
    } catch (err) {
      console.error('🔥 AdminRooms: save error', err);
      setError('Failed to save room');
    }
  };

  const handleEdit = (room) => {
    setEditing(room);
    setForm({
      name: room.name || '',
      building: room.building || '',
      capacity: room.capacity != null ? String(room.capacity) : '',
    });
    setShowForm(true);
  };

  const handleToggle = async (room) => {
    await updateDoc(doc(db, 'rooms', room.id), { isActive: !(room.isActive !== false) });
    dataCacheService.invalidatePath('rooms');
    loadRooms();
  };

  const handleDelete = async (room) => {
    if (!window.confirm(`Delete room "${room.name}"?`)) return;
    await deleteDoc(doc(db, 'rooms', room.id));
    dataCacheService.invalidatePath('rooms');
    loadRooms();
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div><p>Loading rooms…</p></div>;
  }

  return (
    <div className="admin-rooms-view">
      <div className="view-header">
        <h2>Rooms</h2>
        <button className="add-button" onClick={() => { resetForm(); setShowForm(!showForm); }}>
          {showForm ? 'Cancel' : '+ Add Room'}
        </button>
      </div>

      <StatCards items={[
        ['Rooms', rooms.length],
        ['Active', rooms.filter(r => r.isActive !== false).length],
        ['Total Capacity', rooms.reduce((a, r) => a + (r.capacity || 0), 0)],
      ]} />

      <div className="info-banner">
        ℹ️ Rooms are facility spaces at your school — labs, libraries, activity rooms — that classes and subject teachers can use.
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="room-form" onSubmit={handleSave}>
          <div className="form-group">
            <label>Room Name *</label>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Chemistry Lab, Library, Room 12" required />
          </div>
          <div className="form-group half">
            <label>Building</label>
            <input type="text" value={form.building} onChange={e => setForm({ ...form, building: e.target.value })}
              placeholder="e.g., Block A" />
          </div>
          <div className="form-group half">
            <label>Capacity</label>
            <input type="number" min="0" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })}
              placeholder="e.g., 30" />
          </div>
          <button type="submit" className="detail-btn-primary">{editing ? 'Save Changes' : 'Add Room'}</button>
        </form>
      )}

      {rooms.length === 0 ? (
        <div className="empty-state"><p>No rooms yet — add labs, libraries, and other facility rooms.</p></div>
      ) : (
        <div className="rooms-list">
          {rooms.map(r => (
            <div key={r.id} className="room-card">
              <div className="room-icon">🚪</div>
              <div className="room-info">
                <h4>{r.name}</h4>
                <p>{r.building || 'No building'} · Capacity {r.capacity ?? '—'}</p>
              </div>
              <span className={`room-status ${r.isActive !== false ? 'on' : 'off'}`}>
                {r.isActive !== false ? 'Active' : 'Inactive'}
              </span>
              <div className="room-actions">
                <button className="link-button" onClick={() => handleEdit(r)}>Edit</button>
                <button className="link-button" onClick={() => handleToggle(r)}>{r.isActive !== false ? 'Deactivate' : 'Activate'}</button>
                <button className="link-button danger" onClick={() => handleDelete(r)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminRoomsView;
